import { useCallback, useEffect, useMemo, useState } from "react";

import type { PreviewSearchControls } from "./PreviewZoomBar";

import { findMatches, type PdfSearchMatch } from "./pdfSearch";

/** The part of a pdf.js document search reads; `PDFDocumentProxy` satisfies it. */
export interface SearchablePdf {
  numPages: number;
  getPage(pageNumber: number): Promise<{
    getTextContent(): Promise<{ items: ReadonlyArray<object> }>;
  }>;
}

/** A text item's string; marked-content items carry none. */
const itemText = (item: object): string =>
  "str" in item && typeof item.str === "string" ? item.str : "";

/** Every page's text; a page that cannot be read contributes no text, so search still works on the rest. */
async function readPageTexts(pdf: SearchablePdf): Promise<string[][]> {
  const pages = await Promise.allSettled(
    Array.from({ length: pdf.numPages }, async (_, i) => {
      const content = await (await pdf.getPage(i + 1)).getTextContent();
      return content.items.map(itemText);
    })
  );
  return pages.map((page) => (page.status === "fulfilled" ? page.value : []));
}

/**
 * Search over the whole document: text is read once per document, on first search.
 *
 * Each piece of state remembers the document (or match list) it belongs to, so a
 * new document starts over without an effect resetting it.
 */
export function usePdfSearch(pdf: SearchablePdf | null) {
  const [typed, setTyped] = useState<{ pdf: SearchablePdf | null; query: string }>({
    pdf,
    query: "",
  });
  const [texts, setTexts] = useState<{ pdf: SearchablePdf; pages: string[][] } | null>(null);
  const [selected, setSelected] = useState<{ matches: PdfSearchMatch[]; index: number } | null>(
    null
  );

  const query = typed.pdf === pdf ? typed.query : "";
  const pages = texts?.pdf === pdf ? texts.pages : null;
  const needsText = pdf != null && pages == null && query.trim() !== "";

  useEffect(() => {
    if (pdf == null || !needsText) return;
    let cancelled = false;
    // A document that fails to read (or was destroyed) has nothing to match.
    void readPageTexts(pdf)
      .catch((): string[][] => [])
      .then((read) => {
        if (!cancelled) setTexts({ pdf, pages: read });
      });
    return () => {
      cancelled = true;
    };
  }, [pdf, needsText]);

  const matches = useMemo(() => (pages == null ? [] : findMatches(pages, query)), [pages, query]);
  /** The first match until the reader steps, and again whenever the matches change. */
  const activeIndex =
    matches.length === 0 ? null : selected?.matches === matches ? selected.index : 0;

  const onQueryChange = useCallback((next: string) => setTyped({ pdf, query: next }), [pdf]);
  const step = (by: number) => {
    if (activeIndex == null) return;
    setSelected({ matches, index: (activeIndex + by + matches.length) % matches.length });
  };

  const byItem = useMemo(() => {
    const map = new Map<string, PdfSearchMatch[]>();
    for (const m of matches) {
      const key = `${m.pageIndex}:${m.itemIndex}`;
      map.set(key, [...(map.get(key) ?? []), m]);
    }
    return map;
  }, [matches]);

  const matchesByItem = useCallback(
    (pageIndex: number, itemIndex: number) => byItem.get(`${pageIndex}:${itemIndex}`) ?? [],
    [byItem]
  );

  const controls: PreviewSearchControls = {
    query,
    onQueryChange,
    matchCount: matches.length,
    activeIndex,
    onNext: () => step(1),
    onPrevious: () => step(-1),
  };
  return {
    controls,
    activeMatch: activeIndex == null ? null : (matches[activeIndex] ?? null),
    matchesByItem,
  };
}
